import type { VisualizationPresentationMode } from '@/components/tool-visualization-renderer';

interface ArtifactCandidateLike {
  key: string;
  toolName: string;
  presentation: VisualizationPresentationMode;
}

// Browser tools whose results can land in the artifact-candidate list. Every
// one of these is expandable on demand (manual "Expand"), but only an explicit
// allowlist may AUTO-open the drawer. browserLiveView's documented purpose IS
// "open the live browser view in the user's artifact panel", so it auto-opens;
// createBrowser / navigate / scroll (and delegated playwright / connect /
// screenshot) must not hijack the drawer on every tool call.
const BROWSER_TOOL_NAMES = new Set([
  'createBrowser',
  'browserLiveView',
  'browserNavigate',
  'browserScroll',
  'browserPlaywright',
  'connectBrowser',
  'browserScreenshot',
]);

const AUTO_OPEN_BROWSER_ALLOWLIST = new Set(['browserLiveView']);

/**
 * Auto-open policy keyed on the effective tool name. Browser tools may
 * auto-open the drawer ONLY when explicitly allowlisted (browserLiveView).
 * Every non-browser tool keeps its existing presentation-driven behavior.
 * Manual expand is unaffected — this gates auto-open only.
 */
export function canAutoOpenArtifact(toolName: string): boolean {
  if (BROWSER_TOOL_NAMES.has(toolName)) {
    return AUTO_OPEN_BROWSER_ALLOWLIST.has(toolName);
  }
  return true;
}

interface DeriveArtifactPanelStateInput {
  candidates: ArtifactCandidateLike[];
  artifactKey: string | null;
  isArtifactOpen: boolean;
  isDesktop: boolean;
  previousLatestKey: string | null;
}

interface DeriveArtifactPanelStateResult {
  artifactKey: string | null;
  isArtifactOpen: boolean;
  latestArtifactKey: string | null;
  autoOpenLatest: boolean;
  responsiveClose: boolean;
}

export function deriveArtifactPanelState({
  candidates,
  artifactKey,
  isArtifactOpen,
  isDesktop,
  previousLatestKey,
}: DeriveArtifactPanelStateInput): DeriveArtifactPanelStateResult {
  if (candidates.length === 0) {
    return {
      artifactKey: null,
      isArtifactOpen: false,
      latestArtifactKey: null,
      autoOpenLatest: false,
      responsiveClose: false,
    };
  }

  const latestCandidate = candidates[candidates.length - 1];
  const latestChanged = latestCandidate.key !== previousLatestKey;

  if (latestChanged) {
    const presentationAllowsOpen = latestCandidate.presentation !== 'inline';
    const policyAllowsOpen = canAutoOpenArtifact(latestCandidate.toolName);

    // A candidate the policy blocks from auto-opening (a non-allowlisted
    // browser tool such as createBrowser / browserPlaywright) must neither open
    // a closed drawer NOR yank/close an already-open one. Just advance the
    // latest pointer and leave the panel exactly as the user left it (only
    // recovering artifactKey if the current one vanished).
    if (presentationAllowsOpen && !policyAllowsOpen) {
      const nextArtifactKey =
        artifactKey && !candidates.some(candidate => candidate.key === artifactKey)
          ? latestCandidate.key
          : artifactKey;
      return {
        artifactKey: nextArtifactKey,
        isArtifactOpen,
        latestArtifactKey: latestCandidate.key,
        autoOpenLatest: false,
        responsiveClose: false,
      };
    }

    const shouldAutoOpen = isDesktop && presentationAllowsOpen && policyAllowsOpen;
    const shouldFollowLatest = artifactKey === null || artifactKey === previousLatestKey;

    return {
      artifactKey: shouldFollowLatest ? latestCandidate.key : artifactKey,
      isArtifactOpen: shouldFollowLatest ? shouldAutoOpen : isArtifactOpen,
      latestArtifactKey: latestCandidate.key,
      autoOpenLatest: shouldFollowLatest && shouldAutoOpen,
      responsiveClose: false,
    };
  }

  const nextArtifactKey =
    artifactKey && !candidates.some(candidate => candidate.key === artifactKey)
      ? latestCandidate.key
      : artifactKey;

  const responsiveClose = !isDesktop && isArtifactOpen;

  return {
    artifactKey: nextArtifactKey,
    isArtifactOpen: responsiveClose ? false : isArtifactOpen,
    latestArtifactKey: latestCandidate.key,
    autoOpenLatest: false,
    responsiveClose,
  };
}
